import React from "react";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Box, Button, Typography } from "@mui/material";
import { SampleDeal } from "store/types/user";

interface InvestmentCardProps {
  deal: SampleDeal;
}

function InvestmentCard({ deal }: InvestmentCardProps) {
  return (
    <Box
      sx={{
        border: "1.5px solid rgba(10, 49, 66, 0.12)",
        borderRadius: "10px",
        p: 2.5,
        bgcolor: "#fff"
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography sx={{ fontSize: "15px", fontWeight: 500, color: "#0a3142" }}>{deal.title}</Typography>
        {deal.asset_class && (
          <Box
            sx={{
              px: 1.5,
              py: 0.5,
              bgcolor: "#e8f6f7",
              borderRadius: "50px",
              fontSize: "12px",
              fontWeight: 500,
              color: "#26a6ae"
            }}
          >
            {deal.asset_class}
          </Box>
        )}
      </Box>
      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
        {deal.commitment > 0 && (
          <Box>
            <Typography sx={{ fontSize: "12px", color: "rgba(10, 49, 66, 0.5)", mb: 0.5 }}>Commitment</Typography>
            <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#0a3142" }}>
              {new Intl.NumberFormat("en-US", { style: "currency", currency: deal.currency }).format(deal.commitment)}
            </Typography>
          </Box>
        )}
        <Box>
          <Typography sx={{ fontSize: "12px", color: "rgba(10, 49, 66, 0.5)", mb: 0.5 }}>Currency</Typography>
          <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#0a3142" }}>{deal.currency}</Typography>
        </Box>
        {deal.vintage && (
          <Box>
            <Typography sx={{ fontSize: "12px", color: "rgba(10, 49, 66, 0.5)", mb: 0.5 }}>Vintage</Typography>
            <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#0a3142" }}>{deal.vintage}</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

interface SecondUserSummaryStepProps {
  step: number;
  totalSteps: number;
  deals: SampleDeal[];
  submitting?: boolean;
  onBack: () => void;
  onFinish: () => void;
}

export const SecondUserSummaryStep: React.FC<SecondUserSummaryStepProps> = ({
  step,
  totalSteps,
  deals,
  submitting,
  onBack,
  onFinish
}) => {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#ffffff",
        display: "flex",
        flexDirection: "column",
        position: "relative"
      }}
    >
      {/* Back button - top left */}
      <Button
        onClick={onBack}
        startIcon={<ArrowBackIcon sx={{ fontSize: 14, color: "#26a6ae" }} />}
        sx={{
          position: "absolute",
          top: 24,
          left: 32,
          color: "#26a6ae",
          textTransform: "none",
          fontSize: "14px",
          minWidth: "auto",
          p: 0.5,
          "&:hover": {
            opacity: 0.8
          }
        }}
      >
        Back
      </Button>

      {/* Step Counter */}
      <Box
        sx={{
          position: "absolute",
          top: 24,
          right: 32,
          fontSize: "13px",
          color: "rgba(10, 49, 66, 0.5)",
          fontWeight: 500
        }}
      >
        Step {step} of {totalSteps}
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          px: 3
        }}
      >
        <Box sx={{ maxWidth: "640px", width: "100%", textAlign: "center" }}>
          {/* Checkmark */}
          <Box
            sx={{
              display: "inline-flex",
              mb: 3
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 56, color: "#c09560" }} />
          </Box>

          <Typography
            variant="h4"
            sx={{
              fontSize: "28px",
              fontWeight: 500,
              color: "#0a3142",
              mb: 1
            }}
          >
            You&apos;re all set
          </Typography>

          <Typography
            sx={{
              fontSize: "15px",
              color: "rgba(10, 49, 66, 0.6)",
              mb: 3
            }}
          >
            Your account is live. Here&apos;s what the team has on the platform.
          </Typography>

          {/* Investment Cards */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 4, textAlign: "left" }}>
            {deals.map((deal) => (
              <InvestmentCard key={deal.id} deal={deal} />
            ))}
          </Box>

          {/* Action Button */}
          <Button
            variant="contained"
            onClick={onFinish}
            disabled={submitting}
            endIcon={<ArrowForwardIcon />}
            sx={{
              bgcolor: "#071f35",
              boxShadow: "none",
              color: "#fff",
              fontSize: "15px",
              fontWeight: 500,
              textTransform: "none",
              "&:hover": {
                bgcolor: "#0f2d47"
              }
            }}
          >
            Go to my portfolio
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
